/**
 * MetaConfigPage — "Integración Meta (API)" (solo administradores).
 *
 * Pantalla donde el admin captura el token de System User + los IDs de assets
 * (cuenta publicitaria, página, IG business). El token se envía una sola vez por
 * HTTPS y se guarda CIFRADO en el backend; NUNCA se devuelve al frontend (campo
 * write-only). "Probar conexión" hace una llamada real a Meta y muestra el
 * resultado antes de confiar en la fuente en vivo.
 *
 * Una vez conectado, los tableros "Campañas (API)", "Instagram Orgánico" y
 * "Facebook Orgánico" jalan sus datos automáticamente.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  FaFacebook, FaKey, FaCheckCircle, FaTimesCircle, FaSpinner, FaPlug,
  FaSave, FaVial, FaShieldAlt, FaExclamationTriangle, FaTrash, FaEye, FaEyeSlash,
} from 'react-icons/fa';
import { metaApi } from '../../services/metaService';
import MetaExtraCredentials from './MetaExtraCredentials';
import './MetaConfigPage.css';

const EMPTY = { accessToken: '', adAccountId: '', pageId: '', igUserId: '' };

export default function MetaConfigPage() {
  const { user } = useAuthenticator((context) => [context.user]);
  const username = user?.username;

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [status, setStatus] = useState(null);        // estado del backend
  const [form, setForm] = useState(EMPTY);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, details|error}
  const [notice, setNotice] = useState(null);          // {type, msg}

  const applyStatus = useCallback((s) => {
    setStatus(s);
    setForm((f) => ({
      ...f,
      adAccountId: s.ad_account_id || '',
      pageId: s.page_id || '',
      igUserId: s.ig_user_id || '',
      accessToken: '', // el token nunca vuelve: se deja vacío (placeholder indica si hay uno guardado)
    }));
  }, []);

  const loadConfig = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setForbidden(false);
    try {
      const s = await metaApi.getConfig(username);
      applyStatus(s);
    } catch (e) {
      if (e.status === 403) {
        setForbidden(true);
      } else if (e.status === 404) {
        // Aún no hay integración guardada (o el backend todavía no expone la ruta):
        // no es un error, es el estado vacío inicial → "No conectado" sin banner.
        applyStatus({ configured: false, status: 'disconnected' });
      } else {
        setNotice({ type: 'error', msg: e.message });
      }
    } finally {
      setLoading(false);
    }
  }, [username, applyStatus]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const onField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setNotice(null);
    setTestResult(null);
    try {
      const s = await metaApi.saveConfig(username, {
        accessToken: form.accessToken.trim(),   // vacío = conserva el token guardado
        adAccountId: form.adAccountId.trim(),
        pageId: form.pageId.trim(),
        igUserId: form.igUserId.trim(),
      });
      applyStatus(s);
      setNotice({ type: 'ok', msg: 'Configuración guardada.' });
    } catch (e) {
      setNotice({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setNotice(null);
    try {
      const res = await metaApi.testConfig(username);
      setTestResult({ ok: true, details: res.details || {} });
      if (res.status) applyStatus(res);
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
      loadConfig();
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar Meta? Se borrará el token y los tableros (API) dejarán de recibir datos.')) return;
    setSaving(true);
    try {
      await metaApi.deleteConfig(username);
      setForm(EMPTY);
      setStatus({ configured: false, status: 'disconnected' });
      setTestResult(null);
      setNotice({ type: 'ok', msg: 'Meta desconectado.' });
    } catch (e) {
      setNotice({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <main className="meta-page"><div className="meta-state"><FaSpinner className="meta-spin big" /><p>Cargando integración…</p></div></main>;
  }

  if (forbidden) {
    return (
      <main className="meta-page">
        <div className="meta-state">
          <FaShieldAlt className="big" />
          <h3>Acceso restringido</h3>
          <p>La integración con Meta solo puede configurarla un administrador.</p>
        </div>
      </main>
    );
  }

  const connected = status?.configured && status?.status === 'connected';
  const hasError = status?.status === 'error';
  const canSave = form.accessToken.trim() || status?.configured;

  return (
    <main className="meta-page">
      <header className="meta-header">
        <span className="meta-title-icon"><FaFacebook /></span>
        <div>
          <h1 className="meta-title">Integración Meta <span className="meta-badge-api">(API)</span></h1>
          <p className="meta-subtitle">Conecta la API oficial de Meta (Marketing + Graph) con un token de System User. Alimenta las pestañas Campañas (API), Instagram Orgánico y Facebook Orgánico.</p>
        </div>
      </header>

      {/* Estado */}
      <section className={`meta-status-card ${connected ? 'ok' : hasError ? 'err' : 'idle'}`}>
        <div className="meta-status-left">
          <span className="meta-status-dot" />
          <div>
            <div className="meta-status-title">
              {connected ? 'Conectado' : hasError ? 'Error de conexión' : status?.configured ? 'Configurado (sin verificar)' : 'No conectado'}
            </div>
            <div className="meta-status-sub">
              {status?.last_verified_at
                ? `Verificado: ${new Date(status.last_verified_at).toLocaleString('es-MX')}`
                : 'Aún no se ha verificado la conexión.'}
              {hasError && status?.last_error ? ` · ${status.last_error}` : ''}
            </div>
          </div>
        </div>
        {status?.configured && (
          <button className="meta-btn ghost danger" onClick={handleDisconnect} disabled={saving}>
            <FaTrash /> Desconectar
          </button>
        )}
      </section>

      {notice && (
        <div className={`meta-notice ${notice.type}`}>
          {notice.type === 'ok' ? <FaCheckCircle /> : <FaExclamationTriangle />} {notice.msg}
        </div>
      )}

      {/* Formulario */}
      <section className="meta-card">
        <h2 className="meta-card-title"><FaKey /> Credenciales del System User</h2>

        <label className="meta-field">
          <span className="meta-label">Token de System User <em>(write-only)</em></span>
          <div className="meta-token-row">
            <input
              type={showToken ? 'text' : 'password'}
              className="meta-input mono"
              value={form.accessToken}
              onChange={onField('accessToken')}
              placeholder={status?.configured ? '•••••••••• (token guardado — deja vacío para conservarlo)' : 'Pega aquí el token de System User'}
              autoComplete="off"
              spellCheck={false}
            />
            <button type="button" className="meta-eye" onClick={() => setShowToken((v) => !v)} title={showToken ? 'Ocultar' : 'Mostrar'}>
              {showToken ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <span className="meta-hint">Se envía una sola vez y se guarda cifrado. Nunca se muestra de vuelta.</span>
        </label>

        <div className="meta-grid">
          <label className="meta-field">
            <span className="meta-label">Ad Account ID</span>
            <input className="meta-input mono" value={form.adAccountId} onChange={onField('adAccountId')}
              placeholder="act_1234567890, act_9876543210" />
            <span className="meta-hint">
              Campañas pagadas (Marketing API). Puedes poner <strong>varias separadas por coma</strong>:
              el tablero muestra un selector para alternar entre ellas. Útil cuando una agencia
              externa pauta sobre la marca y la cuenta propia queda como histórico. La primera es
              la que se abre por defecto.
            </span>
          </label>
          <label className="meta-field">
            <span className="meta-label">Page ID</span>
            <input className="meta-input mono" value={form.pageId} onChange={onField('pageId')} placeholder="1234567890" />
            <span className="meta-hint">Métricas orgánicas de la Página.</span>
          </label>
          <label className="meta-field">
            <span className="meta-label">Instagram Business ID</span>
            <input className="meta-input mono" value={form.igUserId} onChange={onField('igUserId')} placeholder="17841400000000000" />
            <span className="meta-hint">Insights de Instagram Business.</span>
          </label>
        </div>

        <div className="meta-actions">
          <button className="meta-btn ghost" onClick={handleTest} disabled={testing || !status?.configured}>
            {testing ? <><FaSpinner className="meta-spin" /> Probando…</> : <><FaVial /> Probar conexión</>}
          </button>
          <button className="meta-btn primary" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? <><FaSpinner className="meta-spin" /> Guardando…</> : <><FaSave /> Guardar</>}
          </button>
        </div>

        {testResult && (
          <div className={`meta-test ${testResult.ok ? 'ok' : 'err'}`}>
            {testResult.ok ? (
              <>
                <div className="meta-test-head"><FaCheckCircle /> Token válido</div>
                <ul className="meta-test-list">
                  {testResult.details.app_user && <li>Usuario: <strong>{testResult.details.app_user}</strong></li>}
                  {testResult.details.ad_account_name && <li>Cuenta publicitaria: <strong>{testResult.details.ad_account_name}</strong> ({testResult.details.currency})</li>}
                  {testResult.details.page_name && <li>Página: <strong>{testResult.details.page_name}</strong></li>}
                  {testResult.details.ig_username && <li>Instagram: <strong>@{testResult.details.ig_username}</strong> ({testResult.details.ig_followers} seguidores)</li>}
                  {testResult.details.ad_account_error && <li className="warn">Ads: {testResult.details.ad_account_error}</li>}
                  {testResult.details.page_error && <li className="warn">Página: {testResult.details.page_error}</li>}
                  {testResult.details.ig_error && <li className="warn">Instagram: {testResult.details.ig_error}</li>}
                </ul>
              </>
            ) : (
              <div className="meta-test-head err"><FaTimesCircle /> {testResult.error}</div>
            )}
          </div>
        )}
      </section>

      {/* Tokens adicionales: agencias que pautan desde otro portafolio. */}
      <MetaExtraCredentials username={username} />

      {/* Seguridad / ayuda */}
      <section className="meta-help">
        <h3><FaShieldAlt /> Cómo obtener el token</h3>
        <ol>
          <li>En <strong>Business Manager → Usuarios del sistema</strong>, crea un System User (admin).</li>
          <li>Asígnale los assets: la cuenta publicitaria, la Página y la cuenta de Instagram Business.</li>
          <li>Genera un token con permisos <code>ads_read, read_insights, pages_read_engagement, instagram_basic, instagram_manage_insights, business_management</code> (de larga duración).</li>
          <li>Pégalo arriba junto con los IDs y pulsa <strong>Probar conexión</strong>.</li>
        </ol>
        <p className="meta-help-sec"><FaPlug /> El token se guarda cifrado en el servidor. Todas las llamadas a Meta ocurren en el backend; el token nunca viaja al navegador.</p>
      </section>
    </main>
  );
}
